import moment from 'moment'
import {useEffect, useState, useRef} from 'react'

/**
 * Timer state representing countdown information
 */
export type CountdownTimer =
  | {
      /** Timer is active - within threshold window before deadline */
      isActive: true
      /** Remaining time until deadline */
      remaining: {
        /** Hours remaining (0-23) */
        hours: number
        /** Minutes remaining (0-59) */
        minutes: number
        /** Seconds remaining (0-59) */
        seconds: number
      }
    }
  | {
      /** Timer is not active - outside threshold window or deadline passed */
      isActive: false
    }

/**
 * Options for countdown timer hook
 */
export interface UseCountdownTimerOptions {
  /**
   * UTC timestamp of the deadline (ISO 8601 format)
   * @example '2025-11-25T15:00:00Z'
   */
  until: string | undefined

  /**
   * Timer threshold in ISO 8601 duration format (e.g., '04:00:00' for 4 hours).
   * Timer activates when current time is within this duration before deadline.
   * - If undefined: timer never activates
   * - If '00:00:00': timer activates immediately when deadline exists
   * - If duration: timer activates N time before deadline
   *
   * @example '04:00:00' - Timer shows countdown in last 4 hours
   * @example undefined - No timer
   */
  threshold?: string

  /**
   * Callback triggered when timer expires (deadline reached)
   */
  onExpire?: () => void

  /**
   * Function to get current time for calculations (useful for testing)
   * @default () => moment.utc() - returns current UTC time
   */
  getNow?: () => moment.Moment
}

/**
 * Calculate timer state based on current time and threshold
 */
const calculateTimerState = (
  until: string | undefined,
  threshold: string | undefined,
  now: moment.Moment
): CountdownTimer => {
  if (!until) {
    return {isActive: false}
  }

  const deadline = moment.utc(until)
  const isBeforeDeadline = now.isBefore(deadline)

  if (!isBeforeDeadline) {
    return {isActive: false}
  }

  // No threshold means timer never activates
  if (!threshold) {
    return {isActive: false}
  }

  const thresholdDuration = moment.duration(threshold)
  const isZeroDuration = thresholdDuration.asMilliseconds() === 0

  // Special case: '00:00:00' means timer is always active when before deadline
  const isTimerActive = isZeroDuration
    ? true
    : now.isBetween(deadline.clone().subtract(thresholdDuration), deadline)

  if (!isTimerActive) {
    return {isActive: false}
  }

  // Calculate remaining time for timer display
  const remainingTime = moment.duration(deadline.diff(now))

  return {
    isActive: true,
    remaining: {
      hours: remainingTime.hours(),
      minutes: remainingTime.minutes(),
      seconds: remainingTime.seconds()
    }
  }
}

/**
 * Calculate milliseconds until next state change for smart refresh scheduling
 */
const calculateNextRefreshMs = (
  until: string | undefined,
  threshold: string | undefined,
  now: moment.Moment
): number | null => {
  if (!until) {
    return null
  }

  const deadline = moment.utc(until)
  const msUntilDeadline = deadline.diff(now)

  // If deadline has passed, no refresh needed
  if (msUntilDeadline <= 0) {
    return null
  }

  // Calculate when timer threshold starts
  if (threshold) {
    const thresholdDuration = moment.duration(threshold)
    const thresholdStart = deadline.clone().subtract(thresholdDuration)
    const msUntilThreshold = thresholdStart.diff(now)

    // If we're already past threshold start, just wait for deadline
    if (msUntilThreshold <= 0) {
      return msUntilDeadline
    }

    // Return whichever comes first
    return Math.min(msUntilThreshold, msUntilDeadline)
  }

  return msUntilDeadline
}

/**
 * Reusable countdown timer hook with smart refresh logic.
 *
 * Features:
 * - Smart timeout scheduling: refreshes exactly when state changes (no polling)
 * - 1-second interval only when timer is active (for countdown display)
 * - Automatic cleanup and state management
 * - Customizable threshold for when timer activates
 * - Optional expiration callback
 *
 * @param options - Timer configuration options
 * @returns Current timer state (active with remaining time, or inactive)
 *
 * @example
 * // Timer activates 4 hours before deadline
 * const timer = useCountdownTimer({
 *   until: order.cancellableUntil,
 *   threshold: '04:00:00',
 *   onExpire: () => console.log('Deadline reached!')
 * })
 *
 * if (timer.isActive) {
 *   console.log(`${timer.remaining.hours}h ${timer.remaining.minutes}m left`)
 * }
 *
 * @example
 * // No timer, just expiration tracking
 * const timer = useCountdownTimer({
 *   until: deadline,
 *   onExpire: handleExpire
 * })
 */
export const useCountdownTimer = (options: UseCountdownTimerOptions): CountdownTimer => {
  const {until, threshold, onExpire, getNow = () => moment.utc()} = options

  const [state, setState] = useState<CountdownTimer>(() =>
    calculateTimerState(until, threshold, getNow())
  )

  const hasExpiredRef = useRef(false)

  /**
   * Effect: Reset expired flag when until change
   */
  useEffect(() => {
    hasExpiredRef.current = false
  }, [until])

  /**
   * Effect: Recalculate state when parameters change
   */
  useEffect(() => {
    const newState = calculateTimerState(until, threshold, getNow())
    setState(newState)
  }, [until, threshold])

  /**
   * Effect: Smart timeout - refresh exactly when state will change
   * Avoids polling by calculating exact time until next transition
   */
  useEffect(() => {
    const nextRefreshMs = calculateNextRefreshMs(until, threshold, getNow())

    if (nextRefreshMs === null || nextRefreshMs <= 0) {
      return
    }

    const timeoutId = setTimeout(() => {
      const newState = calculateTimerState(until, threshold, getNow())
      setState(newState)

      // Check if timer just expired
      if (!newState.isActive && !hasExpiredRef.current) {
        hasExpiredRef.current = true
        onExpire?.()
      }
    }, nextRefreshMs)

    return () => clearTimeout(timeoutId)
  }, [until, threshold, state, onExpire])

  /**
   * Effect: 1-second interval when timer is active for countdown display
   * Only runs during active countdown, automatically stops when inactive
   */
  useEffect(() => {
    if (!state.isActive) {
      return
    }

    const intervalId = setInterval(() => {
      const newState = calculateTimerState(until, threshold, getNow())
      setState(newState)

      // Check if timer just expired
      if (!newState.isActive && !hasExpiredRef.current) {
        hasExpiredRef.current = true
        onExpire?.()
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [until, threshold, state.isActive, onExpire])

  return state
}
